import { Autocomplete, TextField } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'

import type { Store } from '@gozoki/api-types'
import { api } from '~/lib/tuyau'

const getServerStoreType = (onlyBoutiques?: boolean, onlyPlatforms?: boolean) => {
  if (onlyBoutiques && !onlyPlatforms) return 'BOUTIQUE'
  if (onlyPlatforms && !onlyBoutiques) return 'WAREHOUSE'
  return undefined
}

export interface StoresPickerProps {
  setCurrentStores?: (value: number[]) => void
  setUniqueStore?: (value: number) => void
  onlyBoutiques?: boolean
  defaultStore?: number
  onlyPlatforms?: boolean
  storesToKeep?: string[]
  allowAllStores?: boolean
  readOnly?: boolean
  usedStores?: Store['id'][]
  extraOptions?: { label: string; value: number }[]
}

export const StoresPicker = ({
  setCurrentStores,
  setUniqueStore,
  onlyBoutiques,
  defaultStore,
  onlyPlatforms,
  storesToKeep,
  allowAllStores,
  usedStores,
  readOnly,
  extraOptions,
}: StoresPickerProps) => {
  const serverStoreType = getServerStoreType(onlyBoutiques, onlyPlatforms)

  const { data: stores, isLoading } = useQuery(
    api.stores.index.queryOptions(serverStoreType ? { query: { storeType: serverStoreType } } : {}),
  )

  const [isInitialized, setIsInitialized] = useState(false)
  const [selectedOption, setSelectedOption] = useState<
    NonNullable<typeof storeSelectOptions>[number] | null
  >(null)
  const allStoresIds = useMemo(() => {
    if (usedStores) {
      return stores?.filter((s) => usedStores.includes(s.id)).map((s) => s.id)
    }
    return stores
      ?.filter((store) => (onlyBoutiques ? store.storeType === 'BOUTIQUE' : true))
      .filter((store) => (onlyPlatforms ? store.storeType === 'WAREHOUSE' : true))
      .filter((store) =>
        storesToKeep ? storesToKeep.some((s) => s === store.checkpointStoreRef) : true,
      )
      .map((store) => store.id)
  }, [onlyBoutiques, onlyPlatforms, stores, storesToKeep, usedStores])

  const realDefaultStore = useMemo(() => {
    if (defaultStore !== undefined) {
      return defaultStore
    }
    // When allowAllStores is true, default to 0 (Toutes les boutiques)
    if (allowAllStores) {
      return 0
    }
    if (allStoresIds?.length) {
      return allStoresIds[0]
    }
    return null
  }, [allStoresIds, defaultStore, allowAllStores])

  useEffect(() => {
    if (isInitialized) {
      return
    }
    if (!allStoresIds || allStoresIds.length === 0) {
      return
    }
    if (setCurrentStores) {
      setCurrentStores(allStoresIds ?? [])
    }
    if (setUniqueStore && realDefaultStore !== null) {
      setUniqueStore(realDefaultStore)
    }
    setIsInitialized(true)
  }, [allStoresIds, realDefaultStore, setCurrentStores, setUniqueStore, isInitialized])

  const storeSelectOptions = useMemo(() => {
    let options: { label: string; value: number }[] | undefined = undefined
    if (usedStores) {
      options = stores
        ?.filter((s) => usedStores.includes(s.id))
        .map((store) => ({
          label: store.label,
          value: store.id,
        }))
    } else {
      options = stores
        ?.filter((store) => (onlyBoutiques ? store.storeType === 'BOUTIQUE' : true))
        .filter((store) => (onlyPlatforms ? store.storeType === 'WAREHOUSE' : true))
        .filter((store) =>
          storesToKeep ? storesToKeep.some((s) => s === store.checkpointStoreRef) : true,
        )
        .map((store) => ({
          label: store.label,
          value: store.id,
        }))
    }
    if (!setUniqueStore || allowAllStores) {
      // Add "Toutes les boutiques" at the beginning when allowAllStores is true
      options?.unshift({ label: 'Toutes les boutiques', value: 0 })
    }
    // Options personnalisées (ex. « En alerte ») préfixées en tête de liste.
    if (extraOptions && extraOptions.length > 0) {
      options?.unshift(...extraOptions)
    }
    return options
  }, [
    allowAllStores,
    extraOptions,
    onlyBoutiques,
    onlyPlatforms,
    setUniqueStore,
    stores,
    storesToKeep,
    usedStores,
  ])

  useEffect(() => {
    if (isLoading || !storeSelectOptions?.length) return
    const defaultVal = defaultStore ?? storeSelectOptions[0]?.value
    setSelectedOption(
      storeSelectOptions.find((o) => o.value === defaultVal) ?? storeSelectOptions[0] ?? null,
    )
  }, [storeSelectOptions, defaultStore, isLoading])

  return (
    <div style={{ display: 'flex', flex: 2, margin: 6 }}>
      <Autocomplete
        fullWidth
        readOnly={readOnly}
        disableClearable={true as boolean}
        options={storeSelectOptions ?? []}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        value={selectedOption}
        noOptionsText="Aucune boutique"
        onChange={(_e, newValue) => {
          setSelectedOption(newValue)
          // we disable clearable so new value should always be different from null
          if (newValue) {
            const value = newValue.value
            if (setUniqueStore) setUniqueStore(value)
            if (setCurrentStores) setCurrentStores(value === 0 ? (allStoresIds ?? []) : [value])
          }
        }}
        renderInput={(params) => <TextField {...params} label="Boutique" />}
      />
    </div>
  )
}
